// src/pages/attendance/components/MonthlyTable.jsx
import React, { useEffect, useMemo } from "react";
import { BarChart3, Clock, TrendingUp } from "lucide-react";
import { Mo3tiCard, Mo3tiProgressBar } from "./mo3tiComponents";
import { getStatusColor, getStatusText, formatTimeForDisplay, calculateWorkingHours } from "../utils/attendanceHelpers";
import { getAttendanceStyles } from "../styles/attendanceStyles";

const MonthlyTable = ({ monthlyAttendance, timeEntries, isLoadingTimeEntries, t, currentTheme, isDarkMode, language, attendanceStats, focusDate }) => {
  const styles = getAttendanceStyles(currentTheme, isDarkMode, language);
  const c = currentTheme.colors;
  const f = currentTheme.fonts;

  // Notification deep-link: scroll the affected day's row into view once the
  // entries have loaded (e.g. an ATTENDANCE_RESET notification's ?date).
  const entryDay = (entry) => String(entry?.date || "").slice(0, 10);
  useEffect(() => {
    if (!focusDate || isLoadingTimeEntries) return;
    if (!timeEntries.some((e) => entryDay(e) === focusDate)) return;
    const timer = setTimeout(() => {
      document.getElementById(`att-day-${focusDate}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate, isLoadingTimeEntries, timeEntries]);

  // performance metrics (moved here from main page)
  const performanceMetrics = useMemo(() => {
    if (!attendanceStats || attendanceStats.length < 4) { 
      return [
        { label: t.performance.attendanceRate, current: 0, target: 95, color: c.success },
        { label: t.performance.punctuality,    current: 0, target: 90, color: c.warning },
        { label: t.performance.avgHoursPerDay, current: 0, target: 8,  color: c.info    },
        { label: t.performance.overtimeHours,  current: 0, target: 10, color: c.primary },
      ];
    }
    const present = parseInt(attendanceStats[0]?.value) || 0;
    const absent  = parseInt(attendanceStats[1]?.value) || 0;
    const late    = parseInt(attendanceStats[2]?.value) || 0;
    const totalHours = parseFloat(String(attendanceStats[3]?.value || "0").split(" ")[0]) || 0;
    const total   = present + absent + late;
    const attended = present + late;
    return [
      { label: t.performance.attendanceRate, current: parseFloat((total > 0 ? (attended / total) * 100 : 0).toFixed(1)), target: 95, color: c.success },
      { label: t.performance.punctuality,    current: parseFloat((attended > 0 ? (present / attended) * 100 : 0).toFixed(1)), target: 90, color: c.warning },
      { label: t.performance.avgHoursPerDay, current: parseFloat((attended > 0 ? totalHours / attended : 0).toFixed(1)),      target: 8,  color: c.info    },
      { label: t.performance.overtimeHours,  current: parseFloat(Math.max(0, totalHours - attended * 8).toFixed(1)),           target: 10, color: c.primary },
    ];
  }, [attendanceStats, c, t]);

  const isRateLabel = (label) =>
    label.includes(language === "ar" ? "معدل" : "Rate") ||
    label.includes(language === "ar" ? "الالتزام" : "Punctuality");

  const formatDuration = (hoursRaw, checkIn, checkOut) => {
    let totalMinutes = 0;

    // Try to get exact minutes from timestamps if available
    if (checkIn && checkOut && checkIn !== "-" && checkOut !== "-" && checkIn !== "undefined" && checkOut !== "undefined") {
      const diffMs = new Date(checkOut) - new Date(checkIn);
      if (diffMs > 0) {
        totalMinutes = Math.round(diffMs / (1000 * 60));
      }
    }

    // Fallback to hoursRaw if timestamps calculation fails
    if (totalMinutes === 0) {
      if (hoursRaw == null || hoursRaw === "" || hoursRaw === "--" || isNaN(parseFloat(hoursRaw))) {
        return "--";
      }
      totalMinutes = Math.round(parseFloat(hoursRaw) * 60);
    }

    if (totalMinutes <= 0) return "--";

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    const hourStr = language === "ar" ? "ساعة" : "hr";
    const minStr = language === "ar" ? "دقيقة" : "min";

    if (h > 0 && m > 0) {
      return `${h} ${hourStr} ${language === "ar" ? "و" : "and"} ${m} ${minStr}`;
    } else if (h > 0) {
      return `${h} ${hourStr}`;
    } else {
      return `${m} ${minStr}`;
    }
  };

  return (
    <>
      {/* Performance metrics */}
      <Mo3tiCard title={t.performance.title} icon={TrendingUp} theme={currentTheme}>
        <div style={styles.metricsGrid}>
          {performanceMetrics.map((m, i) => (
            <div key={i} style={styles.metricCard}>
              <div style={styles.metricHeader}>
                <h4 style={styles.metricTitle}>{m.label}</h4>
                <span style={styles.metricValue(m.color)}>{m.current < 10 ? m.current.toFixed(1) : m.current}{isRateLabel(m.label) ? "%" : ""}</span>
              </div>
              <Mo3tiProgressBar percentage={(m.current / m.target) * 100} color={m.color} theme={currentTheme} showLabel={false} />
              <div style={styles.metricFooter}>
                <span>{t.performance.current} {m.current}{isRateLabel(m.label) ? "%" : ""}</span>
                <span>{t.performance.target} {m.target}{isRateLabel(m.label) ? "%" : ""}</span>
              </div>
            </div>
          ))}
        </div>
      </Mo3tiCard>

      {/* Monthly table */}
      <Mo3tiCard title={t.monthly.title} icon={BarChart3} theme={currentTheme}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>{[t.monthly.month, t.monthly.presentDays, t.monthly.absentDays, t.monthly.lateDays, t.monthly.totalDays, t.monthly.attendanceRate].map((h, i) => (
                <th key={i} style={styles.tableHeader}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {monthlyAttendance.map((month, i) => (
                <tr key={i} style={styles.tableRow}>
                  <td style={styles.tableCell}><div style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{month.month}</div></td>
                  {["present", "absent", "late"].map((key) => (
                    <td key={key} style={styles.tableCell}><div style={styles.statusIndicator(key, c)}>{month[key]}</div></td>
                  ))}
                  <td style={styles.tableCell}>{month.totalDays}</td>
                  <td style={styles.tableCell}>
                    <span style={{ color: month.rate >= 90 ? c.success : month.rate >= 80 ? c.warning : c.danger, fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{month.rate}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Mo3tiCard>

      {/* Recent records */}
      <Mo3tiCard title={t.recent.title} icon={Clock} theme={currentTheme}>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>{[t.recent.date, t.recent.checkIn, t.recent.checkOut, t.recent.totalHours, t.recent.status].map((h, i) => (
                <th key={i} style={styles.tableHeader}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {timeEntries.map((entry, i) => (
                <tr
                  key={i}
                  id={`att-day-${entryDay(entry)}`}
                  style={
                    focusDate && entryDay(entry) === focusDate
                      ? { ...styles.tableRow, outline: `2px solid ${c.primary}`, outlineOffset: "-2px", background: isDarkMode ? "rgba(59, 130, 246, 0.12)" : "rgba(59, 130, 246, 0.08)" }
                      : styles.tableRow
                  }
                >
                  <td style={styles.tableCell}><div style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{new Date(entry.date).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}</div></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{formatTimeForDisplay(entry.checkIn, language)}</span></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.semibold, fontFamily: "'Cairo', sans-serif" }}>{formatTimeForDisplay(entry.checkOut, language)}</span></td>
                  <td style={styles.tableCell}><span style={{ fontWeight: f.weights.bold, color: c.primary, fontFamily: "'Cairo', sans-serif" }}>{formatDuration(entry.totalHours, entry.checkIn, entry.checkOut)}</span></td>
                  <td style={styles.tableCell}>
                    <div style={
                      entry.status === "absent" || entry.status === "leave"
                        ? styles.statusIndicator("absent", c)
                        : styles.statusIndicator(entry.status === "Complete" ? "present" : entry.status, c)
                    }>
                      {getStatusText(entry.status, t)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Mo3tiCard>
    </>
  );
};

export default MonthlyTable;